import { useBusinessEntity, useCompanyData } from '@commenda/api';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Dropdown,
  IconButton,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { ManageAssigneesModal } from '../../assignees';
import { Filing } from '../../filings';

interface FilingAssignedToCardProps {
  filing: Filing;
  onAddAssignees: (assigneeIds: string[]) => Promise<boolean>;
  onRemoveAssignee: (assigneeId: string) => Promise<boolean>;
  onSuccess: () => void;
  className?: string;
  allowMultipleAssignees?: boolean;
  loggedInUserEmail: string;
}

const FilingAssignee = ({
  filing,
  onAddAssignees,
  onRemoveAssignee,
  onSuccess,
  className = '',
  allowMultipleAssignees = true,
  loggedInUserEmail,
}: FilingAssignedToCardProps) => {
  const manageAssigneesModalVisibility = useToggle(false);
  const { data: businessEntity } = useBusinessEntity(filing.corporation.id);
  const { data: companyData } = useCompanyData(
    businessEntity?.generalCorporationInfo.companyId
  );

  const members = companyData?.members || [];
  const availableAssignees: { id: string; name: string; email: string }[] =
    members.map((member) => ({
      id: member.id,
      name: member.name,
      email: member.email,
    }));

  const assigneeNames = filing.assignees?.map((assignee) => assignee.name);

  return (
    <Accordion
      className={`max-w-[316px] w-[316px] rounded-md ${className}`}
      type="single"
      collapsible
      defaultValue="item-1"
    >
      <AccordionItem
        className="rounded-md bg-bg-secondary border border-border-primary"
        value="item-1"
      >
        <AccordionTrigger className="cursor-pointer px-xl py-xl flex justify-between">
          <p className="txt-small-medium">Filing assigned to</p>
        </AccordionTrigger>
        <AccordionContent className="p-xl pt-0 border-t-0 flex flex-col gap-3xl">
          {/* Accountant */}
          {filing.agent && (
            <div className="flex justify-between gap-lg">
              <div className="flex flex-col">
                <span className="txt-small-bold">{filing.agent.name}</span>
                <p className="txt-small-regular text-text-secondary">
                  Accounting partner
                </p>
              </div>
            </div>
          )}

          {/* Internal assignees */}
          <div className="flex justify-between gap-lg">
            <div className="flex flex-col">
              <p className="txt-small-bold">
                {assigneeNames && assigneeNames.length > 0
                  ? assigneeNames.length <= 2
                    ? assigneeNames.join(', ')
                    : `${assigneeNames.slice(0, 2).join(', ')} ...+${
                        assigneeNames.length - 2
                      }`
                  : 'No assignees'}
              </p>
              <p className="txt-small-regular text-text-secondary">
                Internal assignees
              </p>
            </div>
            <Dropdown
              menu={{
                groups: [
                  {
                    items: [
                      {
                        label: 'Manage Assignees',
                        icon: Icons.Users01,
                        onClick: manageAssigneesModalVisibility.show,
                      },
                    ],
                  },
                ],
              }}
              align="end"
            >
              <IconButton
                icon={<Icons.DotsVertical />}
                size="sm"
                aria-label="More options"
              />
            </Dropdown>
          </div>
        </AccordionContent>
      </AccordionItem>

      <ManageAssigneesModal
        isOpen={manageAssigneesModalVisibility.visible}
        onClose={manageAssigneesModalVisibility.toggle}
        filing={filing}
        availableAssignees={availableAssignees}
        onAddAssignees={onAddAssignees}
        onRemoveAssignee={onRemoveAssignee}
        onSuccess={onSuccess}
        allowMultipleAssignees={allowMultipleAssignees}
        loggedInUserEmail={loggedInUserEmail}
      />
    </Accordion>
  );
};

export { FilingAssignee };
